/**
 * Pilcore PMS
 * Admin - Market Intelligence
 * - Admin-only screen to upsert RateShop scope configs (per business unit)
 * - Read-only table with all registered configs
 */

import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";

function parseCSVInts(text) {
  if (!text) return [];
  return String(text)
    .split(",")
    .map((s) => Number(String(s).trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export function parseAdditionalRateShopIds(text, primaryId) {
  if (!String(text || "").trim()) return [];
  const values = String(text).split(",").map((value) => value.trim());
  if (values.some((value) => !/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)) || Number(value) <= 0)) {
    throw new Error("Additional RateShop IDs must be positive whole numbers separated by commas.");
  }
  return [...new Set(values.map(Number))].filter((value) => value !== Number(primaryId));
}

// Expected lines: hotel_code|name|address(optional)
function parseCompetitors(text) {
  const lines = String(text || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const out = [];
  for (const line of lines) {
    const parts = line.split("|").map((p) => p.trim());
    const hotel_code = Number(parts[0]);
    const name = parts[1] || "";
    const address = parts[2] || "";
    if (!hotel_code || !name) continue;
    out.push({ hotel_code, name, address: address || null });
  }
  return out;
}

function formatDateTime(dt) {
  if (!dt) return "–";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

export default function MarketIntelligence() {
  const [configs, setConfigs] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

    const [form, setForm] = useState({
        business_unit_id: "",
        rateshop_id: "",
        additionalRateshopIdsText: "",
        rateshopname: "",
        occupancy: 2,
        currency: "EUR",
        horizon: 90,
        pos: 2,

        // Sources
        sources: [2],
        sources_meta: [{ source_id: 2, source_name: "Booking.com" }],
        sourceDraft: { source_id: "", source_name: "" },

        // Competitors
        competitor_hotels: [],
        competitorDraft: { hotel_code: "", name: "", address: "" },

        // Optional override
        hotelcodesText: "",

        is_active: true,
    });


  const [saving, setSaving] = useState(false);

  const reloadAll = async (reason = "reload") => {
    try {
      console.log("[RATEMETRICS][ADMIN][MarketIntelligence] reloadAll", { reason });

      const [cfgRes, buRes] = await Promise.all([
        axios.get("/api/ratemetrics/rateshop-configs"),
        axios.get("/api/business-units"),
      ]);

      const cfgList = Array.isArray(cfgRes.data) ? cfgRes.data : [];
      const buList = Array.isArray(buRes.data) ? buRes.data : [];

      console.log("[RATEMETRICS][ADMIN][MarketIntelligence] loaded", {
        configs: cfgList.length,
        businessUnits: buList.length,
      });

      setConfigs(cfgList);
      setBusinessUnits(buList);
    } catch (err) {
      console.error("[RATEMETRICS][ADMIN][MarketIntelligence][ERROR] reloadAll failed", err?.response?.data || err);
      setConfigs([]);
      setBusinessUnits([]);
      alert("Failed to load Market Intelligence data.");
    }
  };

  useEffect(() => {
    reloadAll("mount_or_refresh");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const businessUnitMap = useMemo(() => {
    const m = new Map();
    for (const bu of businessUnits) {
      m.set(Number(bu.id), bu);
    }
    return m;
  }, [businessUnits]);

  // Popup mandatory pattern (no intermediate screens)
  useEffect(() => {
    if (!isOpen) return;

    setContentReady(false);

    async function loadEverything() {
      // Ensure BUs are available even if user opens fast
      if (!businessUnits || businessUnits.length === 0) {
        try {
          const buRes = await axios.get("/api/business-units");
          const buList = Array.isArray(buRes.data) ? buRes.data : [];
          setBusinessUnits(buList);
        } catch (err) {
          console.error("[RATEMETRICS][ADMIN][MarketIntelligence][POPUP][ERROR] load BUs failed", err?.response?.data || err);
        }
      }

      // Reset form each time popup opens (admin will paste config)
       setForm({
        business_unit_id: "",
        rateshop_id: "",
        additionalRateshopIdsText: "",
        rateshopname: "",
        occupancy: 2,
        currency: "EUR",
        horizon: 90,
        pos: 2,

        sources: [2],
        sources_meta: [{ source_id: 2, source_name: "Booking.com" }],
        sourceDraft: { source_id: "", source_name: "" },

        competitor_hotels: [],
        competitorDraft: { hotel_code: "", name: "", address: "" },

        hotelcodesText: "",

        is_active: true,
      });


      setContentReady(true);
    }

    loadEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen && !configs) {
    return null;
  }

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

    const addSource = () => {
    const id = Number(form.sourceDraft.source_id);
    const name = String(form.sourceDraft.source_name || "").trim();

    if (!id) return alert("Source ID is required.");
    if (!name) return alert("Source Name is required.");

    setForm((p) => {
      const nextSources = Array.isArray(p.sources) ? [...p.sources] : [];
      const nextMeta = Array.isArray(p.sources_meta) ? [...p.sources_meta] : [];

      if (nextSources.includes(id)) {
        return {
          ...p,
          sourceDraft: { source_id: "", source_name: "" },
        };
      }

      nextSources.push(id);
      nextMeta.push({ source_id: id, source_name: name });

      return {
        ...p,
        sources: nextSources,
        sources_meta: nextMeta,
        sourceDraft: { source_id: "", source_name: "" },
      };
    });
  };

  const removeSource = (sourceId) => {
    const sid = Number(sourceId);
    setForm((p) => ({
      ...p,
      sources: (p.sources || []).filter((x) => Number(x) !== sid),
      sources_meta: (p.sources_meta || []).filter((x) => Number(x.source_id) !== sid),
    }));
  };

  const addCompetitor = () => {
    const hotel_code = Number(form.competitorDraft.hotel_code);
    const name = String(form.competitorDraft.name || "").trim();
    const address = String(form.competitorDraft.address || "").trim();

    if (!hotel_code) return alert("Hotel Code is required.");
    if (!name) return alert("Hotel Name is required.");

    setForm((p) => {
      const list = Array.isArray(p.competitor_hotels) ? [...p.competitor_hotels] : [];
      if (list.some((h) => Number(h.hotel_code) === hotel_code)) {
        return { ...p, competitorDraft: { hotel_code: "", name: "", address: "" } };
      }

      list.push({
        hotel_code,
        name,
        address: address || null,
      });

      return {
        ...p,
        competitor_hotels: list,
        competitorDraft: { hotel_code: "", name: "", address: "" },
      };
    });
  };

  const removeCompetitor = (hotelCode) => {
    const hc = Number(hotelCode);
    setForm((p) => ({
      ...p,
      competitor_hotels: (p.competitor_hotels || []).filter((h) => Number(h.hotel_code) !== hc),
    }));
  };


  const handleSubmit = async () => {
    const business_unit_id = Number(form.business_unit_id);
    const rateshop_id = Number(form.rateshop_id);
    const occupancy = Number(form.occupancy);
    const pos = Number(form.pos);
    const horizon = Number(form.horizon || 90);
    const rateshopname = String(form.rateshopname || "").trim();
    const currency = String(form.currency || "EUR").trim() || "EUR";

    const sources = Array.isArray(form.sources) ? form.sources.map((x) => Number(x)).filter(Boolean) : [];
    const sources_meta = Array.isArray(form.sources_meta) ? form.sources_meta : [];
    const competitor_hotels = Array.isArray(form.competitor_hotels) ? form.competitor_hotels : [];

    const hotelcodesFromText = parseCSVInts(form.hotelcodesText);
    const derivedHotelcodes = competitor_hotels.map((h) => Number(h.hotel_code)).filter(Boolean);
    const hotelcodes = hotelcodesFromText.length > 0 ? hotelcodesFromText : derivedHotelcodes;

    if (!business_unit_id) return alert("Business Unit is required.");
    if (!rateshop_id) return alert("rateshop_id is required.");
    let additional_rateshop_ids;
    try {
      additional_rateshop_ids = parseAdditionalRateShopIds(form.additionalRateshopIdsText, rateshop_id);
    } catch (error) {
      return alert(error.message);
    }
    if (!rateshopname) return alert("rateshopname is required.");
    if (!occupancy || occupancy < 1) return alert("Occupancy must be >= 1.");
    if (!pos) return alert("POS is required.");
    if (!sources || sources.length === 0) return alert("At least one Source is required.");
    if (!sources_meta || sources_meta.length === 0) return alert("Source Name is required (sources_meta).");
    if (sources_meta.some((s) => !s?.source_id || !String(s?.source_name || "").trim())) {
      return alert("All sources must include both Source ID and Source Name.");
    }

    const metaIds = new Set(sources_meta.map((s) => Number(s.source_id)));
    const missingMeta = sources.filter((id) => !metaIds.has(Number(id)));
    if (missingMeta.length > 0) return alert(`Missing Source Name for: ${missingMeta.join(", ")}`);

    if (!competitor_hotels || competitor_hotels.length === 0) {
      return alert("At least one Competitor is required.");
    }
    if (!hotelcodes || hotelcodes.length === 0) return alert("Hotel codes cannot be empty.");

    const payload = {
      business_unit_id,
      rateshop_id,
      additional_rateshop_ids,
      rateshopname,
      occupancy,
      currency,
      horizon,
      pos,

      sources,
      sources_meta,

      hotelcodes,
      competitor_hotels,

      is_active: form.is_active === false ? false : true,

      raw_config: {
        rateshopid: rateshop_id,
        additional_rateshop_ids,
        rateshopname,
        occupancy,
        currency,
        horizon,
        hotelcodes,
        sources,
        pos,
      },

      created_by: null,
    };


    try {
      setSaving(true);
      console.log("[RATEMETRICS][ADMIN][MarketIntelligence][POST] upsert config", payload);

      await axios.post("/api/ratemetrics/rateshop-configs", payload);

      console.log("[RATEMETRICS][ADMIN][MarketIntelligence][POST][OK] upsert done");
      alert("Saved successfully.");

      handleClose();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("[RATEMETRICS][ADMIN][MarketIntelligence][POST][ERROR] upsert failed", err?.response?.data || err);
      alert("Failed to save config.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-info mb-0">Market Intelligence</h5>

        <button
          type="button"
          className="btn btn-sm fw-bold text-white"
          style={{
            background: "linear-gradient(to right, #1abc9c, #3498db)",
            borderRadius: "5px",
            padding: "6px 14px",
            border: "none",
          }}
          onClick={handleOpen}
        >
          Add / Update RateShop Config
        </button>
      </div>

      {configs.length === 0 ? (
        <p className="fst-italic text-secondary">No RateShop configs registered yet.</p>
      ) : (
        <div
          className="table-responsive"
          style={{
            maxHeight: "500px",
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <table
            className="table table-dark table-striped table-sm align-middle mb-0"
            style={{
              fontSize: "0.78rem",
              lineHeight: "1.0rem",
              tableLayout: "auto",
              width: "auto",
              minWidth: "1100px",
              whiteSpace: "nowrap",
            }}
          >
            <thead>
              <tr>
                <th>Business Unit</th>
                <th className="text-end">RateShop ID</th>
                <th>Additional RateShop IDs</th>
                <th>RateShop Name</th>
                <th className="text-end">Occupancy</th>
                <th className="text-end">POS</th>
                <th className="text-end">Horizon</th>
                <th>Currency</th>
                <th>Sources</th>
                <th>Competitors</th>
                <th className="text-end">Updated</th>
                <th className="text-center">Active</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((c) => {
                const bu = businessUnitMap.get(Number(c.business_unit_id));
                const buLabel = bu?.name || `BU ${c.business_unit_id}`;
                const competitors = Array.isArray(c.competitor_hotels) ? c.competitor_hotels : [];
                const sources = Array.isArray(c.sources) ? c.sources : [];
                const sourcesMeta = Array.isArray(c.sources_meta) ? c.sources_meta : [];
                const updated = c.updated_at || c.updatedAt || null;

                return (
                  <tr key={c.id}>
                    <td style={{ whiteSpace: "normal", minWidth: 220 }}>{buLabel}</td>
                    <td className="text-end">{c.rateshop_id}</td>
                    <td>{(c.additional_rateshop_ids || []).join(", ") || "–"}</td>
                    <td style={{ whiteSpace: "normal", minWidth: 220 }}>{c.rateshopname}</td>
                    <td className="text-end">{c.occupancy}</td>
                    <td className="text-end">{c.pos}</td>
                    <td className="text-end">{c.horizon}</td>
                    <td>{c.currency}</td>
                    <td style={{ whiteSpace: "normal", minWidth: 220 }}>
                      {sourcesMeta.length > 0
                        ? sourcesMeta.map((s, idx) => (
                            <div key={`${c.id}-src-${idx}`}>
                              <span className="text-white">{s.source_name}</span>
                              <span className="text-white-50"> ({s.source_id})</span>
                            </div>
                          ))
                        : sources.join(", ")}
                    </td>
                    <td style={{ whiteSpace: "normal", minWidth: 320 }}>
                      {competitors.map((h, idx) => (
                        <div key={`${c.id}-h-${idx}`}>
                          <span className="text-white">{h.name}</span>
                          <span className="text-white-50"> ({h.hotel_code})</span>
                          {h.address ? <span className="text-white-50"> — {h.address}</span> : null}
                        </div>
                      ))}
                    </td>
                    <td className="text-end">{formatDateTime(updated)}</td>
                    <td className="text-center">{c.is_active ? "Yes" : "No"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Popup */}
      {isOpen && contentReady && (
        <div className="inv-popup-overlay">
          <div className="inv-popup-container bg-dark text-light p-4 rounded" style={{ width: "720px", maxHeight: "calc(100vh - 120px)", overflowY: "auto" }}>
            <h5 className="text-warning text-center mb-4">Register RateShop Config</h5>

            <div className="mb-3">
              <label className="form-label">Business Unit</label>
              <select
                className="form-select form-select-sm"
                value={form.business_unit_id}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const existing = configs.find((config) => Number(config.business_unit_id) === Number(selectedId));
                  setForm((p) => ({
                    ...p,
                    business_unit_id: selectedId,
                    additionalRateshopIdsText: (existing?.additional_rateshop_ids || []).join(", "),
                  }));
                }}
              >
                <option value="">Select...</option>
                {businessUnits.map((bu) => (
                  <option key={bu.id} value={bu.id}>
                    {bu.name || `BU ${bu.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="row g-2">
              <div className="col-4">
                <label className="form-label">RateShop ID</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={form.rateshop_id}
                  onChange={(e) => setForm((p) => ({ ...p, rateshop_id: e.target.value }))}
                />
              </div>
              <div className="col-8">
                <label className="form-label">RateShop Name</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.rateshopname}
                  onChange={(e) => setForm((p) => ({ ...p, rateshopname: e.target.value }))}
                />
              </div>
            </div>

            <div className="mt-2">
              <label className="form-label" htmlFor="additional-rateshop-ids">Additional RateShop IDs (optional)</label>
              <input
                id="additional-rateshop-ids"
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g. 29679652, 29679653"
                value={form.additionalRateshopIdsText}
                onChange={(e) => setForm((p) => ({ ...p, additionalRateshopIdsText: e.target.value }))}
              />
              <small className="text-white-50">
                Add these only when the 1-night and 2-night downloads use different RateShop IDs.
                Prices for 1-night and 2-night stays are identified automatically from received data.
              </small>
            </div>

            <div className="row g-2 mt-2">
              <div className="col-4">
                <label className="form-label">Occupancy</label>
                <input
                  type="number"
                  min="1"
                  className="form-control form-control-sm"
                  value={form.occupancy}
                  onChange={(e) => setForm((p) => ({ ...p, occupancy: e.target.value }))}
                />
              </div>
              <div className="col-4">
                <label className="form-label">POS</label>
                <input
                  type="number"
                  min="1"
                  className="form-control form-control-sm"
                  value={form.pos}
                  onChange={(e) => setForm((p) => ({ ...p, pos: e.target.value }))}
                />
              </div>
              <div className="col-4">
                <label className="form-label">Horizon</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  className="form-control form-control-sm"
                  value={form.horizon}
                  onChange={(e) => setForm((p) => ({ ...p, horizon: e.target.value }))}
                />
              </div>
            </div>

            <div className="row g-2 mt-2">
              <div className="col-3">
                <label className="form-label">Currency</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={form.currency}
                  onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                />
              </div>
                            <div className="col-12">
                <label className="form-label">Sources</label>

                <div className="row g-2">
                  <div className="col-3">
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      placeholder="Source ID (e.g. 2)"
                      value={form.sourceDraft.source_id}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          sourceDraft: { ...p.sourceDraft, source_id: e.target.value },
                        }))
                      }
                    />
                  </div>

                  <div className="col-7">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Source Name (e.g. Booking.com)"
                      value={form.sourceDraft.source_name}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          sourceDraft: { ...p.sourceDraft, source_name: e.target.value },
                        }))
                      }
                    />
                  </div>

                  <div className="col-2 d-grid">
                    <button type="button" className="btn btn-sm btn-primary" onClick={addSource}>
                      Add Source
                    </button>
                  </div>
                </div>

                <div className="mt-2">
                  {(form.sources_meta || []).length === 0 ? (
                    <div className="text-white-50" style={{ fontSize: "0.85rem" }}>
                      No sources added yet.
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ overflowX: "auto" }}>
                      <table
                        className="table table-dark table-striped table-sm align-middle mb-0"
                        style={{
                          fontSize: "0.78rem",
                          lineHeight: "1.0rem",
                          tableLayout: "auto",
                          width: "auto",
                          minWidth: "600px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <thead>
                          <tr>
                            <th className="text-end">ID</th>
                            <th>Name</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.sources_meta.map((s) => (
                            <tr key={`src-${s.source_id}`}>
                              <td className="text-end">{s.source_id}</td>
                              <td style={{ whiteSpace: "normal" }}>{s.source_name}</td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removeSource(s.source_id)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

            </div>

            <div className="mt-2">
              <label className="form-label">Hotel Codes (optional, comma-separated)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Leave empty to derive from competitors"
                value={form.hotelcodesText}
                onChange={(e) => setForm((p) => ({ ...p, hotelcodesText: e.target.value }))}
              />
            </div>

                        <div className="mt-3">
              <label className="form-label">Competitors</label>

              <div className="row g-2">
                <div className="col-3">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Hotel Code"
                    value={form.competitorDraft.hotel_code}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        competitorDraft: { ...p.competitorDraft, hotel_code: e.target.value },
                      }))
                    }
                  />
                </div>

                <div className="col-5">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Hotel Name"
                    value={form.competitorDraft.name}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        competitorDraft: { ...p.competitorDraft, name: e.target.value },
                      }))
                    }
                  />
                </div>

                <div className="col-4">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Address (optional)"
                    value={form.competitorDraft.address}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        competitorDraft: { ...p.competitorDraft, address: e.target.value },
                      }))
                    }
                  />
                </div>

                <div className="col-12 d-grid mt-1">
                  <button type="button" className="btn btn-sm btn-primary" onClick={addCompetitor}>
                    Add Competitor
                  </button>
                </div>
              </div>

              <div className="mt-2">
                {(form.competitor_hotels || []).length === 0 ? (
                  <div className="text-white-50" style={{ fontSize: "0.85rem" }}>
                    No competitors added yet.
                  </div>
                ) : (
                  <div className="table-responsive" style={{ overflowX: "auto" }}>
                    <table
                      className="table table-dark table-striped table-sm align-middle mb-0"
                      style={{
                        fontSize: "0.78rem",
                        lineHeight: "1.0rem",
                        tableLayout: "auto",
                        width: "auto",
                        minWidth: "800px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <thead>
                        <tr>
                          <th className="text-end">Hotel Code</th>
                          <th>Hotel Name</th>
                          <th>Address</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.competitor_hotels.map((h) => (
                          <tr key={`comp-${h.hotel_code}`}>
                            <td className="text-end">{h.hotel_code}</td>
                            <td style={{ whiteSpace: "normal", minWidth: 220 }}>{h.name}</td>
                            <td style={{ whiteSpace: "normal", minWidth: 260 }}>{h.address || "–"}</td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeCompetitor(h.hotel_code)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>


            <div className="form-check form-switch mt-3">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="ratemetrics-is-active"
                checked={!!form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
              <label className="form-check-label text-white-50" htmlFor="ratemetrics-is-active">
                Active
              </label>
            </div>

            <div className="d-flex justify-content-between mt-4">
              <button type="button" className="btn btn-sm btn-secondary" onClick={handleClose} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-sm btn-success" onClick={handleSubmit} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && !contentReady ? null : null}
    </div>
  );
}
